import { z } from 'zod';

/**
 * Air-support REQUEST + routing to the MNRF/AFFES fire centre (#445 workstream 1,
 * migration 0145).
 *
 * In Ontario a waterbomber / air-support aircraft is an MNRF/AFFES (Aviation, Forest Fire
 * and Emergency Services) asset, TASKED by the MNRF fire centre — a municipality / Field IC
 * REQUESTS air support, routed TO the fire centre; it NEVER tasks or dispatches aircraft
 * (Ontario PERP §6.7.1 — "request assistance … the ministry decides deployment"). This is
 * that request's home: an operator-authored REQUEST record the COP captures ON BEHALF OF the
 * requesting authority and routes to the fire centre via the #490 delivery bridge —
 * records-not-confers, the COP records the request; it does NOT task the aircraft.
 *
 * DOCTRINE MODEL (encoded so a generic COP can't get it wrong):
 *  - Only the MNRF/AFFES fire centre TASKS air support (PERP §6.7.1). The seat REQUESTS. NO
 *    seat affordance says "task / dispatch / launch / send / scramble / divert / assign"
 *    aircraft — ever. The request states are ONLY Requested / Routed to fire centre / Not yet
 *    routed — NEVER "tanker dispatched / en route / on station / water drop in progress"
 *    (that would fabricate fulfilment the COP cannot observe — the ADS-B layer is not wired
 *    as fulfilment).
 *  - BOTH the municipal and First-Nation paths request TO the fire centre (a First-Nation
 *    request is ALSO a request to MNRF/AFFES — never a direct tasker).
 *  - An air-support request is a PARALLEL SIBLING to a declaration + an evacuation order + a
 *    public-alert request. It is NOT required for any and is NOT modelled as a parent.
 *  - This clones the #497/#461 public-alert-request rails (a SHORT request lifecycle:
 *    active → rescinded | stood_down), NOT the drone accept/enroute/flown billing machine.
 *
 * FOREIGN-VALUE GUARD: the request's hazard/incident reference renders ONLY the COP's OWN
 * doctrine-safe identity — the linked incident's incident_number + the doctrine-safe
 * HAZARD_LABEL map. NO raw CAP field ever renders on the request surface.
 *
 * Operator vocabulary is doctrine-strict (per-surface rule §4.1: UI = strict verbatim) and
 * pinned by air-support-request.lock.test.ts + apps/web/src/ontarioImsVocabulary.lock.test.ts.
 */

const UUID = z.string().uuid();

/** The KIND of requesting authority. BOTH paths request TO the fire centre. */
export const AIR_SUPPORT_AUTHORITY_KINDS = ['municipal', 'first_nation'] as const;
export const AirSupportAuthorityKindSchema = z.enum(AIR_SUPPORT_AUTHORITY_KINDS);
export type AirSupportAuthorityKind = z.infer<typeof AirSupportAuthorityKindSchema>;

/** Operator-facing labels for the requesting-authority-kind. */
export const AIR_SUPPORT_AUTHORITY_KIND_LABELS: Record<AirSupportAuthorityKind, string> = {
  municipal: 'Municipality',
  first_nation: 'First Nation',
};

/** The requested air-support resource — the COP's OWN vocabulary. "Waterbomber" ONLY;
 *  NEVER "air tanker / air attack officer / bird dog" (US-wildfire-agency terms). */
export const AIR_SUPPORT_REQUESTED_RESOURCES = ['waterbomber', 'helicopter', 'recon'] as const;
export const AirSupportRequestedResourceSchema = z.enum(AIR_SUPPORT_REQUESTED_RESOURCES);
export type AirSupportRequestedResource = z.infer<typeof AirSupportRequestedResourceSchema>;

/** Operator-facing labels for the requested resource. */
export const AIR_SUPPORT_REQUESTED_RESOURCE_LABELS: Record<AirSupportRequestedResource, string> = {
  waterbomber: 'Waterbomber',
  helicopter: 'Helicopter',
  recon: 'Reconnaissance aircraft',
};

/** Lifecycle: 'active' (request stands) | 'rescinded' (withdrawn) | 'stood_down' (need passed). */
export const AirSupportRequestStatusSchema = z.enum(['active', 'rescinded', 'stood_down']);
export type AirSupportRequestStatus = z.infer<typeof AirSupportRequestStatusSchema>;

/** Operator-facing status labels. */
export const AIR_SUPPORT_STATUS_LABELS: Record<AirSupportRequestStatus, string> = {
  active: 'Active',
  rescinded: 'Rescinded',
  stood_down: 'Stood down',
};

/**
 * The MNRF/AFFES fire centre — the destination an air-support request routes to. NAME must
 * match the migration-0145 delivery_destinations seed exactly (route-side resolution is by
 * NAME, the #517 ISC precedent). LABEL is the short form bound into the affordance.
 */
export const AIR_SUPPORT_FIRE_CENTRE_NAME = 'MNRF/AFFES Provincial Fire Centre';
export const AIR_SUPPORT_FIRE_CENTRE_LABEL = 'MNRF/AFFES';

/**
 * Operator-facing affordance copy — doctrine-strict. A REQUEST routed TO the fire centre,
 * NEVER a tasking/dispatch. The seat NEVER says "task / dispatch / launch / send / scramble /
 * divert / assign" aircraft (PERP §6.7.1 — only the fire centre tasks). Pinned by
 * air-support-request.lock.test.ts (editing them is founder authorization).
 */
export const REQUEST_AIR_SUPPORT_LABEL = 'Request air support';
/** The affordance with the fire centre BOUND INTO the label (records-not-confers cue). */
export const REQUEST_AIR_SUPPORT_FROM_FIRE_CENTRE_LABEL = `Request air support — from ${AIR_SUPPORT_FIRE_CENTRE_LABEL}`;
export const RESCIND_AIR_SUPPORT_REQUEST_LABEL = 'Rescind air-support request';
export const STAND_DOWN_AIR_SUPPORT_REQUEST_LABEL = 'Stand down air-support request';
export const AIR_SUPPORT_REQUEST_HEADING = 'Air Support Request';

/**
 * The records-not-confers footer the request surface states (PERP §6.7.1). Three short
 * sentences, ordered so no tasking verb ever directly governs an aircraft noun (the seat
 * never claims to task the aircraft): "No aircraft are tasked …" (aircraft is the SUBJECT),
 * "Air support is tasked only by MNRF/AFFES" (attribution to the tasker). Pinned by the lock.
 */
export const AIR_SUPPORT_RECORDS_NOT_CONFERS_NOTE =
  'No aircraft are tasked, dispatched, or launched by this request. Air support is tasked only by MNRF/AFFES; this records a request routed to the fire centre.';

/**
 * The PERP §6.7.1 records-not-confers copy the request surface states. tasker_note attributes
 * tasking to MNRF/AFFES (never the seat); request_note states records-not-confers; live_route
 * names the fire-centre route. Deliberately phrased so no tasking verb directly governs an
 * aircraft noun (the seat never claims to task the aircraft).
 */
export const AIR_SUPPORT_REQUEST_FIRE_CENTRE_COPY = {
  tasker_note: 'Air support is tasked only by MNRF/AFFES (Ontario PERP §6.7.1).',
  request_note:
    'This records a request routed to the MNRF/AFFES fire centre; no aircraft are tasked by it.',
  live_route: 'routed to the MNRF/AFFES fire centre for tasking consideration',
} as const;

/**
 * The ONLY request-side states (BLOCKING HONESTY): the request is Requested, then Routed to
 * fire centre {ts}, else Not yet routed. NEVER "tanker dispatched / en route / on station /
 * water drop in progress" — the COP cannot observe fulfilment and the ADS-B feed is not wired
 * as one. Pinned by the honesty guard in air-support-request.lock.test.ts.
 */
export const AIR_SUPPORT_REQUESTED_STATE_LABEL = 'Requested';
export const AIR_SUPPORT_ROUTED_STATE_PREFIX = 'Routed to fire centre';
export const AIR_SUPPORT_NOT_ROUTED_STATE_LABEL = 'Not yet routed';

/* ------------------------------------------------------------------ *
 * CI-lock guard data (exported so BOTH air-support-request.lock.test.ts AND
 * apps/web/src/ontarioImsVocabulary.lock.test.ts scan with ONE source of truth).
 * ------------------------------------------------------------------ */

/**
 * FORBIDDEN_AIR_DISPATCH_DRIFT — no operator string may read as the COP tasking/dispatching/
 * launching/sending/scrambling/diverting/assigning aircraft, nor coin a US-ICS air-operations
 * title. Mirrors FORBIDDEN_ALERT_ISSUANCE_DRIFT (non-inert: each row carries a `sample` the
 * guard must catch). The tasking-verb pattern is DIRECT-GOVERNMENT (verb immediately governs an
 * aircraft noun) so the records-not-confers copy — where the aircraft noun is the SUBJECT ("no
 * aircraft are tasked …") or tasking is attributed to MNRF/AFFES — PASSES, while an affirmative
 * seat claim ("Dispatch the waterbomber", "Task aircraft", "Scramble a helicopter") FAILS.
 */
export const FORBIDDEN_AIR_DISPATCH_DRIFT: ReadonlyArray<{
  readonly name: string;
  readonly pattern: RegExp;
  readonly sample: string;
}> = [
  {
    name: 'the COP tasks/dispatches/launches/sends/scrambles/diverts/assigns/deploys aircraft (PERP §6.7.1 — only the MNRF/AFFES fire centre tasks; the seat REQUESTS). Direct-government form: the verb immediately governs an aircraft noun.',
    pattern:
      /\b(task|tasks|tasked|tasking|dispatch|dispatches|dispatched|dispatching|launch|launches|launched|launching|send|sends|sending|scramble|scrambles|scrambled|scrambling|divert|diverts|diverted|diverting|assign|assigns|assigned|assigning|deploy|deploys|deployed|deploying)\s+(the\s+|a\s+|an\s+|these\s+|its\s+|their\s+|our\s+|air\s+)?(aircraft|air\s?support|waterbombers?|water\s?bombers?|helicopters?|tankers?|planes?)\b/i,
    sample: 'Dispatch the waterbomber',
  },
  {
    name: '"air tanker" (US-wildfire-agency term — Ontario says "waterbomber")',
    pattern: /\bair[\s-]?tankers?\b/i,
    sample: 'air tanker',
  },
  {
    name: '"bird dog" (US air-attack lead-plane slang — never an operator term)',
    pattern: /\bbird[\s-]?dogs?\b/i,
    sample: 'bird dog',
  },
  {
    name: '"air attack officer" (US-ICS air-attack role — Ontario names no such operator role)',
    pattern: /\bair\s+attack\s+officers?\b/i,
    sample: 'Air Attack Officer',
  },
  {
    name: '"Air Operations Branch Director" (US-ICS — not an Ontario IMS operator title)',
    pattern: /\bair\s+operations\s+branch\s+directors?\b/i,
    sample: 'Air Operations Branch Director',
  },
  {
    name: '"Air Tactical Group Supervisor" (US-ICS — not an Ontario IMS operator title)',
    pattern: /\bair\s+tactical\s+group\s+supervisors?\b/i,
    sample: 'Air Tactical Group Supervisor',
  },
  {
    name: '"Air Support Group Supervisor" (US-ICS — not an Ontario IMS operator title)',
    pattern: /\bair\s+support\s+group\s+supervisors?\b/i,
    sample: 'Air Support Group Supervisor',
  },
];

/**
 * FORBIDDEN_AIR_SUPPORT_FULFILMENT_DRIFT — BLOCKING HONESTY: an air-support REQUEST surface
 * cannot claim fulfilment the COP does not observe. No request string may read as the aircraft
 * being en route / on station / airborne / making a water drop. (Scans only the air-support
 * constants — not the incident vocabulary — so "en route" on an unrelated surface is unaffected.)
 */
export const FORBIDDEN_AIR_SUPPORT_FULFILMENT_DRIFT: ReadonlyArray<{
  readonly name: string;
  readonly pattern: RegExp;
  readonly sample: string;
}> = [
  {
    name: '"en route" (fabricated fulfilment — the COP records a REQUEST, it does not track the aircraft flying)',
    pattern: /\ben[\s-]?route\b/i,
    sample: 'tanker en route',
  },
  {
    name: '"on station" (fabricated fulfilment — the request is Routed / Not yet routed, never on station)',
    pattern: /\bon[\s-]?station\b/i,
    sample: 'aircraft on station',
  },
  {
    name: '"airborne" / "wheels up" (fabricated fulfilment)',
    pattern: /\b(airborne|wheels[\s-]?up)\b/i,
    sample: 'wheels up',
  },
  {
    name: '"water drop" (fabricated fulfilment — the COP never observes the drop from a request record)',
    pattern: /\bwater[\s-]?drops?\b/i,
    sample: 'water drop in progress',
  },
  {
    name: '"inbound" fulfilment framing (the request is Routed / Not yet routed, never "inbound")',
    pattern: /\binbound\b/i,
    sample: 'tanker inbound',
  },
];

/* ------------------------------------------------------------------ *
 * Request — POST /api/air-support-requests (routes to the fire centre)
 * ------------------------------------------------------------------ */

export const AirSupportRequestCreateSchema = z.object({
  session_id: z.string().min(1).max(128),
  /** BOTH 'municipal' and 'first_nation' are accepted (each is a request TO the fire centre). */
  requesting_authority_kind: AirSupportAuthorityKindSchema.default('municipal'),
  /** The requesting jurisdiction — a REAL place (municipality / First Nation name). */
  requesting_authority_name: z.string().min(1).max(200),
  /** The named requester — optional free text. */
  requester_name: z.string().max(200).optional(),
  /** Optional PARALLEL-SIBLING context link to the hazard/incident (never a precondition). */
  incident_id: UUID.optional(),
  /** The requested resource — the COP's OWN vocabulary (waterbomber default). */
  requested_resource: AirSupportRequestedResourceSchema.default('waterbomber'),
  /** The COP's OWN hazard enum — never a raw CAP field. */
  hazard_type: z.string().max(80).optional(),
  notes: z.string().max(2000).optional(),
  /** Explicit registry destination id; omitted → the MNRF/AFFES fire-centre destination (by name). */
  destination_id: UUID.optional(),
});
export type AirSupportRequestCreate = z.infer<typeof AirSupportRequestCreateSchema>;

/** POST /api/air-support-requests/:id/rescind — flip active → rescinded + route a withdrawal. */
export const AirSupportRequestRescindSchema = z.object({
  session_id: z.string().min(1).max(128),
  reason: z.string().max(2000).optional(),
  destination_id: UUID.optional(),
});
export type AirSupportRequestRescind = z.infer<typeof AirSupportRequestRescindSchema>;

/** POST /api/air-support-requests/:id/stand-down — flip active → stood_down + route a stand-down. */
export const AirSupportRequestStandDownSchema = z.object({
  session_id: z.string().min(1).max(128),
  reason: z.string().max(2000).optional(),
  destination_id: UUID.optional(),
});
export type AirSupportRequestStandDown = z.infer<typeof AirSupportRequestStandDownSchema>;

/* ------------------------------------------------------------------ *
 * Read shape — a plain record (geography is optional; not served as GeoJSON here)
 * ------------------------------------------------------------------ */

export const AirSupportRequestRecordSchema = z.object({
  id: z.string(),
  request_number: z.string(),
  requesting_authority_kind: AirSupportAuthorityKindSchema,
  requesting_authority_name: z.string(),
  requester_name: z.string().nullable(),
  incident_id: z.string().nullable(),
  /** The COP's OWN hazard enum — the surface maps it through HAZARD_LABEL (never raw CAP). */
  hazard_type: z.string().nullable(),
  requested_resource: AirSupportRequestedResourceSchema,
  status: AirSupportRequestStatusSchema,
  requested_at: z.string(),
  rescinded_at: z.string().nullable(),
  rescind_reason: z.string().nullable(),
  stood_down_at: z.string().nullable(),
  stand_down_reason: z.string().nullable(),
  /** When the request was routed to the fire centre (the honest "Routed" state). */
  fire_centre_notified_at: z.string().nullable(),
  notes: z.string().nullable(),
  /** The recorder (the operator who captured it in-COP) — ORTHOGONAL to the requester. */
  created_by_operator_id: z.string().nullable(),
});
export type AirSupportRequestRecord = z.infer<typeof AirSupportRequestRecordSchema>;

/** The request/rescind/stand-down response — the record + the fire-centre routing outcome. */
export const AirSupportRequestResultSchema = z.object({
  request: AirSupportRequestRecordSchema,
  delivery_channel: z.enum(['webhook', 'email', 'console_log']),
  destination_name: z.string().nullable(),
});
export type AirSupportRequestResult = z.infer<typeof AirSupportRequestResultSchema>;
